<template>
    <form-modal
        ref="formModal"
        :v="$v.modalBusinessUnit"
        :title="i18nTitleModal"
        :handle-hide="cleanModal"
        :in-editing="isEdit || toggleStatusOperation === 'inactivate'"
        size="xl"
        :handle-close="handleCancel">
        <template v-slot:form>
            <form @submit.prevent="handleOk">
                <confirmation-form-modal
                    ref="confirmationFormModal"
                    :title="i18nBusinessUnit"
                    :operation="isEdit ? modalOperation : (isToggleStatus ? toggleStatusOperation + '_business_unit' : 'deleted')"
                />
                <feedback-modal
                    ref="feedbackModal"
                    :title="i18nBusinessUnit"
                />
                <selectCsvModal
                    ref="posCsvModal"
                    :title="i18nSelectThroughCsv"
                    :service="'pos'"
                    csv-type="point_of_sales_external_code"
                    :type-fields="selectFields.point_of_sales_external_code"
                    @csvCreated="loadPosByJson"
                />
                <selectCsvModal
                    ref="userCsvModal"
                    :title="i18nSelectThroughCsv"
                    :service="'users'"
                    csv-type="users_email"
                    :type-fields="selectFields.users_email"
                    @csvCreated="loadUsersByJson"
                />
                <b-row v-if="!isEdit && isBusinessUnitCreated" class="mb-2">
                    <div class="col-sm-4 form-inline">
                    </div>
                    <div class="col-sm-8">
                        <div class="text-sm-right business-unit-modal__toolbar-actions">
                            <edit-label-button
                                v-if="$can('UPDATE', 'BUSINESSUNIT')"
                                :title="getI18n(i18nKey, 'TITLES.business_unit')"
                                :busy="editLoading"
                                :disabled="deleteLoading || toggleStatusLoading"
                                @onClick="handleEdit"
                            />
                            <inactivate-label-button
                                v-if="$can('UPDATE', 'BUSINESSUNIT') && modalBusinessUnit.is_active"
                                :title="getI18n(i18nKey, 'TITLES.business_unit')"
                                :busy="toggleStatusLoading"
                                @onClick="handleToggleStatus('inactivate')"
                            />
                            <activate-label-button
                                v-if="$can('UPDATE', 'BUSINESSUNIT') && !modalBusinessUnit.is_active"
                                :title="getI18n(i18nKey, 'TITLES.business_unit')"
                                :busy="toggleStatusLoading"
                                @onClick="handleToggleStatus('activate')"
                            />
                            <delete-label-button
                                v-if="$can('DELETE', 'BUSINESSUNIT')"
                                :title="getI18n(i18nKey, 'TITLES.business_unit')"
                                :busy="deleteLoading"
                                :disabled="editLoading || toggleStatusLoading"
                                @onClick="handleDelete"
                            />
                        </div>
                    </div>
                </b-row>
                <b-row v-if="isToggleStatus && toggleStatusOperation === 'inactivate'">
                    <b-col md="12">
                        <b-form-group
                            :label="i18nInactivateReason"
                            label-for="modal-name"
                            class="required label-pdv"
                            :invalid-feedback="getI18n('ERROR_CODES.invalid_inactivation_reason')"
                        >
                            <b-form-textarea
                                id="modal-inactivate-reason"
                                v-model.trim="$v.modalToggleStatus.inactivation_reason.$model"
                                class="input-pdv-blue"
                                name="inactivation_reason"
                                aria-describedby="input-1-live-feedback"
                                :disabled="submitLoading"
                                :state="validateField('inactivation_reason', 'modalToggleStatus')"
                                :placeholder="i18nInactivateReason"
                                autofocus
                                @input="clearResponseError('inactivation_reason', 'modalToggleStatus')"
                                @blur="$v.modalToggleStatus.inactivation_reason.$touch"
                            />
                            </b-form-group>
                    </b-col>
                </b-row>
                <b-tabs
                    v-if="!isToggleStatus || toggleStatusOperation === 'activate' "
                    id="businessunit-tabs"
                    v-model="tabIndex"
                    class="tabs-pdv"
                    @activate-tab="tabActivated"
                >
                    <b-tab :title="i18nInformation">
                        <b-row>
                            <b-col md="12">
                                <b-form-group
                                    :label="i18nName"
                                    label-for="modal-name"
                                    class="required label-pdv"
                                    :invalid-feedback="getI18n('ERROR_CODES.invalid_name')"
                                >
                                    <b-form-input
                                        id="modal-name"
                                        v-model.trim="$v.modalBusinessUnit.name.$model"
                                        class="input-pdv-blue"
                                        name="name"
                                        aria-describedby="input-1-live-feedback"
                                        type="text"
                                        :disabled="!isEdit || submitLoading"
                                        :state="validateField('name', 'modalBusinessUnit')"
                                        :placeholder="i18nName"
                                        autofocus
                                        autocomplete="off"
                                        @input="clearResponseError('name', 'modalBusinessUnit')"
                                        @blur="$v.modalBusinessUnit.name.$touch"
                                    />
                                </b-form-group>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col md="4">
                                <b-form-group
                                    :label="i18nContractValue"
                                    label-for="modal-contract-value"
                                    class="label-pdv"
                                    :invalid-feedback="getI18n('ERROR_CODES.invalid_contract_value')"
                                >
                                    <money
                                        id="modal-contract-value"
                                        v-model.trim="$v.modalBusinessUnit.contract_value.$model"
                                        v-bind="money"
                                        name="contract_value"
                                        class="form-control input-pdv-blue"
                                        :class="validationClass($v.modalBusinessUnit.contract_value)"
                                        aria-describedby="input-1-live-feedback"
                                        :disabled="!isEdit || submitLoading"
                                        type="text"
                                        :state="validateField('contract_value','modalBusinessUnit')"
                                        @blur.native="$v.modalBusinessUnit.contract_value.$touch"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col md="4">
                                <b-form-group
                                    :label="i18nContractTime"
                                    label-for="modal-contract-time"
                                    class="label-pdv"
                                    :invalid-feedback="getI18n('ERROR_CODES.invalid_contract_time')"
                                >
                                    <b-form-input
                                        id="modal-contract-time"
                                        v-model.trim="$v.modalBusinessUnit.contract_time.$model"
                                        name="contract_time"
                                        aria-describedby="input-1-live-feedback"
                                        type="number"
                                        min="0"
                                        class="input-pdv-blue"
                                        :state="validateField('contract_time','modalBusinessUnit')"
                                        :disabled="!isEdit || submitLoading"
                                        @input="clearResponseError('contract_time','modalBusinessUnit')"
                                        @blur="$v.modalBusinessUnit.contract_time.$touch"
                                    />
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </b-tab>
                    <b-tab>
                        <template v-slot:title>
                            <b-spinner
                                v-if="loadingPos"
                                class="mr-1"
                                type="border"
                                small
                            />
                            {{ i18nPos }}
                        </template>
                        <b-form-row v-if="isEdit" class="d-flex justify-content-end">
                            <import-button
                                :disabled="!posLoaded || loadingPos || submitLoading"
                                @onClick="showPosCsvModal"
                            />
                            <delete-all-button
                                :disabled="!posLoaded || loadingPos || submitLoading"
                                @onClick="handleRemoveAllPos"
                            />
                        </b-form-row>
                        <b-form-row>
                            <b-col v-if="isEdit" class="col-sm-12 col-md-4">
                                <b-form-group
                                    :label="i18nPos"
                                    label-for="add-pos-input"
                                    class="label-pdv" >
                                    <multiSelectWithService
                                        :id="'add-pos-input'"
                                        ref="posMultiselect"
                                        v-model="posAddForm.selectedPos"
                                        :service="'pos'"
                                        :searchable="true"
                                        :multiple="false"
                                        :disabled="!posLoaded || loadingPos || modalBusinessUnit.add_all_pos || submitLoading"
                                    />
                                </b-form-group>
                                <b-form-group
                                    :label="i18nPosContractTimeMinutes"
                                    label-for="add-pos-contract-time"
                                    class="label-pdv"
                                    :invalid-feedback="getI18n('ERROR_CODES.invalid_contract_time')"
                                >
                                    <b-form-input
                                        id="add-pos-contract-time"
                                        v-model.trim="$v.posAddForm.contract_time.$model"
                                        type="number"
                                        min="1"
                                        class="input-pdv-blue"
                                        :state="validateField('contract_time', 'posAddForm')"
                                        :disabled="!posLoaded || loadingPos || modalBusinessUnit.add_all_pos || submitLoading"
                                        :placeholder="posAddContractTimePlaceholder"
                                        @input="clearResponseError('contract_time', 'posAddForm')"
                                        @blur="$v.posAddForm.contract_time.$touch"
                                    />
                                </b-form-group>
                                <b-form-group
                                    :label="i18nPosVisitValue"
                                    label-for="add-pos-contract-value"
                                    class="label-pdv"
                                    :invalid-feedback="getI18n('ERROR_CODES.invalid_contract_value')"
                                >
                                    <money
                                        id="add-pos-contract-value"
                                        v-model.trim="$v.posAddForm.contract_value.$model"
                                        v-bind="money"
                                        name="add-pos-contract-value"
                                        class="form-control input-pdv-blue"
                                        :class="validationClass($v.posAddForm.contract_value)"
                                        type="text"
                                        :state="validateField('contract_value', 'posAddForm')"
                                        :disabled="!posLoaded || loadingPos || modalBusinessUnit.add_all_pos || submitLoading"
                                        :placeholder="posAddContractValuePlaceholder"
                                        @blur.native="$v.posAddForm.contract_value.$touch"
                                    />
                                </b-form-group>
                                <b-form-group
                                    :label="i18nPosVisitFrequency"
                                    label-for="add-pos-visit-frequency"
                                    class="label-pdv"
                                    :invalid-feedback="getI18n('ERROR_CODES.invalid_frequency')"
                                >
                                    <b-form-input
                                        id="add-pos-visit-frequency"
                                        v-model.trim="$v.posAddForm.visit_frequency.$model"
                                        type="number"
                                        min="1"
                                        class="input-pdv-blue"
                                        :state="validateField('visit_frequency', 'posAddForm')"
                                        :disabled="!posLoaded || loadingPos || modalBusinessUnit.add_all_pos || submitLoading"
                                        @input="clearResponseError('visit_frequency', 'posAddForm')"
                                        @blur="$v.posAddForm.visit_frequency.$touch"
                                    />
                                </b-form-group>
                                <add-button
                                    ref="addPosButton"
                                    :disabled="!posLoaded || loadingPos || modalBusinessUnit.add_all_pos || submitLoading"
                                    @onClick="handleAddPos"
                                />
                                <b-form-group
                                    class="label-pdv"
                                    :label="i18nAddAllPos"
                                    label-for="add-all-pos"
                                >
                                    <b-form-checkbox
                                        id="add-all-pos"
                                        v-model="modalBusinessUnit.add_all_pos"
                                        size="lg"
                                        switch
                                        :disabled="!posLoaded || loadingPos || submitLoading"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col :class="isEdit ? 'col-sm-12 col-md-8' : 'col-sm-12 col-md-12'">
                                <data-mode-vuetable
                                    ref="relationVuetable"
                                    v-model="modalBusinessUnit.point_of_sales"
                                    :fields="posFields"
                                    :display-delete-button="isEdit"
                                    :busy="loadingPos"
                                    :disabled="submitLoading"
                                    @onClickDelete="handleRemovePos"
                                >
                                    <template v-slot:cell(contract_time)="{ item }">
                                        <div
                                            class="business-unit-pos-editable-cell"
                                            :class="{ 'business-unit-pos-editable-cell--invalid': posInlineInvalid(item, 'contract_time') }"
                                            @click="startPosInlineEdit(item, 'contract_time')"
                                        >
                                            <template v-if="posInlineEditing(item, 'contract_time')">
                                                <b-form-input
                                                    ref="posInlineInput"
                                                    v-model="posInlineEdit.draft"
                                                    type="number"
                                                    min="1"
                                                    size="sm"
                                                    class="input-pdv-blue"
                                                    @blur="commitPosInlineEdit(item, 'contract_time')"
                                                    @keydown.esc.prevent="cancelPosInlineEdit"
                                                />
                                            </template>
                                            <template v-else>
                                                <span
                                                    class="business-unit-pos-editable-cell__text"
                                                    :class="{ 'text-muted': posPivotCellMuted(item, 'contract_time') }"
                                                >{{ formatPosContractTimeCell(item) }}</span>
                                                <i
                                                    v-if="posPivotUsesFallback(item, 'contract_time')"
                                                    :id="'pos-inherit-ct-' + item.id"
                                                    class="fe-layers business-unit-pos-editable-cell__inherit-icon ml-1"
                                                />
                                                <b-tooltip
                                                    v-if="posPivotUsesFallback(item, 'contract_time')"
                                                    :target="'pos-inherit-ct-' + item.id"
                                                    triggers="hover"
                                                >
                                                    {{ i18nInheritedFromBu }}
                                                </b-tooltip>
                                            </template>
                                        </div>
                                    </template>
                                    <template v-slot:cell(contract_value)="{ item }">
                                        <div
                                            class="business-unit-pos-editable-cell"
                                            :class="{ 'business-unit-pos-editable-cell--invalid': posInlineInvalid(item, 'contract_value') }"
                                            @click="startPosInlineEdit(item, 'contract_value')"
                                        >
                                            <template v-if="posInlineEditing(item, 'contract_value')">
                                                <money
                                                    ref="posInlineMoney"
                                                    v-model="posInlineEdit.draft"
                                                    v-bind="money"
                                                    class="form-control form-control-sm input-pdv-blue"
                                                    @blur.native="commitPosInlineEdit(item, 'contract_value')"
                                                    @keydown.native.esc.prevent="cancelPosInlineEdit"
                                                />
                                            </template>
                                            <template v-else>
                                                <span
                                                    class="business-unit-pos-editable-cell__text"
                                                    :class="{ 'text-muted': posPivotCellMuted(item, 'contract_value') }"
                                                >{{ formatPosContractValueCell(item) }}</span>
                                                <i
                                                    v-if="posPivotUsesFallback(item, 'contract_value')"
                                                    :id="'pos-inherit-cv-' + item.id"
                                                    class="fe-layers business-unit-pos-editable-cell__inherit-icon ml-1"
                                                />
                                                <b-tooltip
                                                    v-if="posPivotUsesFallback(item, 'contract_value')"
                                                    :target="'pos-inherit-cv-' + item.id"
                                                    triggers="hover"
                                                >
                                                    {{ i18nInheritedFromBu }}
                                                </b-tooltip>
                                            </template>
                                        </div>
                                    </template>
                                    <template v-slot:cell(visit_frequency)="{ item }">
                                        <div
                                            class="business-unit-pos-editable-cell"
                                            :class="{ 'business-unit-pos-editable-cell--invalid': posInlineInvalid(item, 'visit_frequency') }"
                                            @click="startPosInlineEdit(item, 'visit_frequency')"
                                        >
                                            <template v-if="posInlineEditing(item, 'visit_frequency')">
                                                <b-form-input
                                                    ref="posInlineInputVf"
                                                    v-model="posInlineEdit.draft"
                                                    type="number"
                                                    min="1"
                                                    size="sm"
                                                    class="input-pdv-blue"
                                                    @blur="commitPosInlineEdit(item, 'visit_frequency')"
                                                    @keydown.esc.prevent="cancelPosInlineEdit"
                                                />
                                            </template>
                                            <template v-else>
                                                <span
                                                    class="business-unit-pos-editable-cell__text"
                                                    :class="{ 'text-muted': posPivotCellMuted(item, 'visit_frequency') }"
                                                >{{ formatPosVisitFrequencyCell(item) }}</span>
                                                <i
                                                    v-if="posPivotUsesFallback(item, 'visit_frequency')"
                                                    :id="'pos-inherit-vf-' + item.id"
                                                    class="fe-layers business-unit-pos-editable-cell__inherit-icon ml-1"
                                                />
                                                <b-tooltip
                                                    v-if="posPivotUsesFallback(item, 'visit_frequency')"
                                                    :target="'pos-inherit-vf-' + item.id"
                                                    triggers="hover"
                                                >
                                                    {{ i18nInheritedFromBu }}
                                                </b-tooltip>
                                            </template>
                                        </div>
                                    </template>
                                </data-mode-vuetable>
                            </b-col>
                        </b-form-row>
                    </b-tab>
                    <b-tab>
                        <template v-slot:title>
                            <b-spinner
                                v-if="loadingUsers"
                                class="mr-1"
                                type="border"
                                small
                            />
                            {{ i18nUsers }}
                        </template>
                        <b-form-row>
                            <b-col md="12">
                                <b-alert show variant="info">
                                    <i class="fe-info icon-blue mr-1" />
                                    {{ i18nServiceCostModuleInfo }}.
                                </b-alert>
                            </b-col>
                        </b-form-row>
                        <b-form-row v-if="isEdit" class="d-flex justify-content-end">
                            <import-button
                                :disabled="!usersLoaded || loadingUsers || submitLoading"
                                @onClick="showUserCsvModal"
                            />
                            <delete-all-button
                                :disabled="!usersLoaded || loadingUsers || submitLoading"
                                @onClick="handleRemoveAllUser"
                            />
                        </b-form-row>
                        <b-form-row>
                            <b-col v-if="isEdit" class="col-sm-12 col-md-4">
                                <b-form-group
                                    :label="i18nUsers"
                                    label-for="modal-user-user"
                                    class="required label-pdv">
                                    <multiSelectWithService
                                        :id="'modal-user-user'"
                                        ref="userMultiselect"
                                        v-model="userModal.user"
                                        :input-function="touchUserModalUserId"
                                        :select-class="validationClass($v.userModal.user.id)"
                                        :service="'users'"
                                        :searchable="true"
                                        :multiple="false"
                                        :disabled="!usersLoaded || loadingUsers || modalBusinessUnit.add_all_users || submitLoading"
                                        :parameters="usersParameters"
                                    />
                                </b-form-group>
                                <b-form-group
                                    :label="i18nMonthlyCost"
                                    label-for="modal-user-monthly-cost"
                                    class="label-pdv"
                                    :invalid-feedback="getI18n('ERROR_CODES.invalid_contract_value')"
                                >
                                    <money
                                        id="modal-user-monthly-cost"
                                        v-model.trim="userModal.monthly_cost"
                                        v-bind="money"
                                        name="monthly-cost"
                                        class="form-control input-pdv-blue"
                                        :class="validationClass($v.userModal.monthly_cost)"
                                        aria-describedby="input-1-live-feedback"
                                        :disabled="!usersLoaded || loadingUsers || modalBusinessUnit.add_all_users || submitLoading"
                                        :state="validateField('monthly_cost', 'userModal')"
                                    />
                                </b-form-group>
                                <b-form-group
                                    :label="i18nHoursDaily"
                                    label-for="modal-user-hours_daily"
                                    class="label-pdv"
                                    :invalid-feedback="getI18n('ERROR_CODES.invalid_contract_time')"
                                >
                                    <b-form-input
                                        id="modal-user-hours_daily"
                                        v-model.trim="$v.userModal.hours_daily.$model"
                                        name="contract_time"
                                        aria-describedby="input-1-live-feedback"
                                        type="number"
                                        min="0"
                                        class="input-pdv-blue"
                                        :state="validateField('hours_daily','userModal')"
                                        :disabled="!usersLoaded || loadingUsers || modalBusinessUnit.add_all_users || submitLoading"
                                        @input="clearResponseError('hours_daily','userModal')"
                                        @blur="$v.userModal.hours_daily.$touch"
                                    />
                                </b-form-group>
                                <add-button
                                    ref="addUserButton"
                                    :disabled="!usersLoaded || loadingUsers || modalBusinessUnit.add_all_users || submitLoading"
                                    @onClick="handleAddUser"
                                />
                                <b-form-group
                                    class="label-pdv mt-3"
                                    :label="i18nAddAllUser"
                                    label-for="add-all-users"
                                >
                                    <b-form-checkbox
                                        id="add-all-users"
                                        v-model="modalBusinessUnit.add_all_users"
                                        size="lg"
                                        switch
                                        :disabled="!usersLoaded || loadingUsers || submitLoading"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col :class="isEdit ? 'col-sm-12 col-md-8' : 'col-sm-12 col-md-12'">
                                <data-mode-vuetable
                                    ref="relationVuetable"
                                    v-model="modalBusinessUnit.users"
                                    :fields="userFields"
                                    :display-delete-button="isEdit"
                                    :busy="loadingUsers"
                                    :disabled="submitLoading"
                                    @onClickDelete="handleRemoveUser"
                                />
                            </b-col>
                        </b-form-row>
                    </b-tab>
                </b-tabs>
            </form>
        </template>
        <template v-slot:footer>
            <div v-if="isToggleStatus && toggleStatusOperation === 'inactivate'" class="d-flex justify-content-center">
                <cancel-button
                    ref="cancelButton"
                    child-class="col-sm-4 float-left"
                    :disabled="toggleStatusLoading"
                    @onClick="handleCancelToggleStatus"
                />
                <save-button
                    ref="okButton"
                    child-class="col-sm-4 float-right ml-3"
                    :busy="toggleStatusLoading"
                    :disabled="toggleStatusLoading"
                    @onClick="handleToggleStatusConfirmation"
                />
            </div>
            <div v-if="isEdit" class="d-flex justify-content-center">
                <cancel-button
                    ref="cancelButton"
                    child-class="col-sm-4 float-left"
                    :disabled="submitLoading"
                    @onClick="handleCancel"
                />
                <save-button
                    v-if="tabIndex !== 2"
                    ref="okButton"
                    child-class="col-sm-4 float-right ml-3"
                    :title="getI18n('COMMON.next')"
                    :busy="submitLoading"
                    @onClick="tabIndex++"
                />
                <save-button
                    v-else-if="isBusinessUnitCreated"
                    ref="okButton"
                    child-class="col-sm-4 float-right ml-3"
                    :busy="submitLoading"
                    :disabled="!posLoaded || !usersLoaded"
                    @onClick="handleOk"
                />
                <add-button
                    v-else
                    ref="okButton"
                    child-class="col-sm-4 float-right ml-3"
                    :busy="submitLoading"
                    :disabled="!posLoaded || !usersLoaded"
                    @onClick="handleOk"
                />
            </div>
        </template>
    </form-modal>
</template>
<script>
import { Money } from 'v-money'

import ActivateLabelButton from '@/src/components/ActivateLabelButton.vue'
import AddButton from '@/src/components/AddButton'
import CancelButton from '@/src/components/CancelButton'
import DataModeVuetable from '@/src/components/DataModeVuetable.vue'
import DeleteAllButton from '@/src/components/DeleteAllButton.vue'
import DeleteLabelButton from '@/src/components/DeleteLabelButton.vue'
import EditLabelButton from '@/src/components/EditLabelButton.vue'
import ImportButton from '@/src/components/ImportButton.vue'
import InactivateLabelButton from '@/src/components/InactivateLabelButton.vue'
import SaveButton from '@/src/components/SaveButton'
import FormModal from '@components/FormModal'
import ConfirmationFormModal from '@src/components/ConfirmationFormModal'
import FeedbackModal from '@src/components/FeedbackModal'
import multiSelectWithService from '@src/components/multiSelectWithService.vue'
import SelectCsvModal from '@src/components/SelectCsvModal'
import { i18n } from '@src/i18n'
import csvFields from '@src/mixins/csvFields'
import csvParse from '@src/mixins/csvParse'
import swalFeedback from '@src/mixins/swalFeedback'
import validationMixin from '@src/mixins/validation'
import vuetableFormatters from '@src/mixins/vuetableFormatters'
import BusinessUnitsService from '@src/services/BusinessUnitsService'
import PosService from '@src/services/PosService'
import { unionBy, omit } from 'lodash'
import {
    decimal,
    integer,
    maxLength,
    minLength,
    minValue,
    required,
} from 'vuelidate/lib/validators'

const i18nCommon = 'COMMON'
const i18nKey = 'BUSINESS_UNITS'

function optionalPosContractTime(value) {
    if (value === '' || value === null || value === undefined) {
        return true
    }
    return integer(value) && minValue(1)(value)
}

function optionalPosContractValue(value) {
    if (value === '' || value === null || value === undefined) {
        return true
    }
    return decimal(value) && minValue(0)(value)
}

function optionalPosVisitFrequency(value) {
    if (value === '' || value === null || value === undefined) {
        return true
    }
    return integer(value) && minValue(1)(value)
}

export default {
    components: {
        FormModal,
        ConfirmationFormModal,
        FeedbackModal,
        SelectCsvModal,
        Money,
        CancelButton,
        AddButton,
        ImportButton,
        SaveButton,
        EditLabelButton,
        DeleteLabelButton,
        InactivateLabelButton,
        ActivateLabelButton,
        DeleteAllButton,
        DataModeVuetable,
        multiSelectWithService
    },
    mixins: [
        validationMixin,
        swalFeedback,
        csvFields,
        csvParse,
        vuetableFormatters,
    ],
    props: {
        modalIsEdit: {
            required: true,
            type: Boolean,
        },
        modal: {
            type: Object,
            default: () => {
                return {
                    id: '',
                    name: '',
                    contract_value: 0,
                    contract_time: 0,
                    add_all_users: false,
                    add_all_pos: false,
                    users: [],
                    point_of_sales: [],
                    is_active: true,
                }
            },
        },
    },
    data() {
        return {
            i18nCommon,
            i18nKey,
            modalBusinessUnit: this.modal,
            modalToggleStatus: {
                inactivation_reason: '',
            },
            userModal: {
                user: {},
                monthly_cost: 0,
                hours_daily: 0,
            },
            tabIndex: 0,
            toggleStatusOperation: null,
            isToggleStatus: false,
            isEdit: false,
            editLoading: false,
            submitLoading: false,
            deleteLoading: false,
            toggleStatusLoading: false,
            loadingUsers: false,
            loadingPos: false,
            usersLoaded: false,
            posLoaded: false,
            money: {
                decimal: i18n.t('CURRENCY.decimal'),
                thousands: i18n.t('CURRENCY.thousands'),
                prefix: i18n.t('CURRENCY.prefix'),
                suffix: i18n.t('CURRENCY.suffix'),
                precision: 2,
                masked: false,
            },
            posAddForm: {
                selectedPos: {},
                contract_time: '',
                contract_value: '',
                visit_frequency: '',
            },
            posInlineEdit: {
                itemId: null,
                field: null,
                draft: '',
                backup: null,
            },
            posInlineInvalidMap: {},
            posEscBoundHandler: null,
            posFields: [
                {
                    key: 'external_code',
                    label: this.getI18n(i18nCommon, 'external_code'),
                    sortable: true,
                },
                {
                    key: 'name',
                    label: this.getI18n('POS', 'TITLES.pos'),
                    sortable: true,
                },
                {
                    key: 'contract_time',
                    label: this.getI18n(i18nKey, 'TITLES.pos_contract_time_minutes'),
                    sortable: true,
                },
                {
                    key: 'contract_value',
                    label: this.getI18n(i18nKey, 'TITLES.pos_visit_value'),
                    sortable: true,
                },
                {
                    key: 'visit_frequency',
                    label: this.getI18n(i18nKey, 'TITLES.pos_visit_frequency'),
                    sortable: true,
                },
                {
                    key: 'actions',
                    label: '',
                },
            ],
            userFields: [
                {
                    key: 'name',
                    label: this.getI18n(i18nCommon, 'name'),
                    sortable: true,
                },
                {
                    key: 'email',
                    label: this.getI18n(i18nCommon, 'email'),
                    sortable: true,
                },
                {
                    key: 'monthly_cost',
                    label: this.getI18n('USERS', 'FIELDS.monthly_cost'),
                    sortable: true,
                    formatter: this.getI18nCurrency,
                },
                {
                    key: 'hours_daily',
                    label: this.getI18n('USERS', 'FIELDS.hours_daily'),
                    sortable: true,
                    formatter: this.formatHours,
                },
                {
                    key: 'actions',
                    label: '',
                },
            ],
            usersParameters: {
                per_page: 1000,
                page: 1,
                orderBy: 'name',
                sortedBy: 'asc',
                active: true,
            },
        }
    },
    validations: {
        modalBusinessUnit: {
            name: {
                required,
                minLength: minLength(2),
                maxLength: maxLength(255),
            },
            contract_value: {
                decimal,
                minValue: minValue(0),
            },
            contract_time: {
                integer,
                minValue: minValue(0),
            },
        },
        modalToggleStatus: {
            inactivation_reason: {
                required,
                maxLength: maxLength(255),
            },
        },
        userModal: {
            user: {
                id: {
                    required,
                    integer,
                    minValue: minValue(1)
                },
            },
            monthly_cost: {
                decimal,
                minValue: minValue(0),
            },
            hours_daily: {
                integer,
                minValue: minValue(0)
            },
        },
        posAddForm: {
            selectedPos: {
                id: {
                    required,
                    integer,
                    minValue: minValue(1),
                },
            },
            contract_time: {
                optionalPosContractTime,
            },
            contract_value: {
                optionalPosContractValue,
            },
            visit_frequency: {
                optionalPosVisitFrequency,
            },
        },
    },
    computed: {
        isBusinessUnitCreated() {
            return this.modalBusinessUnit.id > 0
        },
        i18nTitleModal() {
            if (this.isBusinessUnitCreated) {
                if (this.isEdit) {
                    return `${this.getI18n(i18nCommon, 'edit')} ${this.i18nBusinessUnit}`
                }
                return this.modalBusinessUnit.name
            }
            else {
                return this.getI18n(i18nKey, 'BUTTONS.new_business_unit')
            }
        },
        i18nName() {
            return this.getI18n(i18nCommon, 'name')
        },
        i18nInactivateReason() {
            return this.getI18n(i18nCommon, 'inactivation_reason')
        },
        i18nBusinessUnit() {
            return this.getI18n(i18nKey, 'TITLES.business_unit')
        },
        i18nContractValue() {
            return this.getI18n(i18nKey, 'TITLES.contract_value')
        },
        i18nContractTime() {
            return this.getI18n(i18nKey, 'TITLES.contract_time')
        },
        i18nAddAllUser() {
            return this.getI18n(i18nKey, 'TITLES.add_all_user')
        },
        i18nAddAllPos() {
            return this.getI18n(i18nKey, 'TITLES.add_all_pos')
        },
        i18nSelectThroughCsv() {
            return this.getI18n('QUESTIONNAIRE_FILTERS', 'TITLES.select_through_csv')
        },
        i18nNoRecordsFound() {
            return this.getI18n('PAGINATION', 'no_records_found')
        },
        i18nMonthlyCost() {
            return this.getI18n('USERS', 'FIELDS.monthly_cost')
        },
        i18nHoursDaily() {
            return this.getI18n('USERS', 'FIELDS.hours_daily')
        },
        i18nInformation() {
            return this.getI18nModified({
                prefix: 'COMMON.info',
                modifier: 2,
            })
        },
        i18nUsers() {
            return this.getI18nModified({
                prefix: 'USERS',
                suffix: 'TITLES.user',
                modifier: 2,
            })
        },
        i18nPos() {
            return this.getI18nModified({
                prefix: 'POS',
                suffix: 'TITLES.pos',
                modifier: 2,
            })
        },
        i18nSuccessfullyProcessed() {
            return this.getI18nModified({
                prefix: 'RESPONSES',
                suffix: 'successfully_processed',
                modifier: 3,
            })
        },
        i18nServiceCostModuleInfo() {
            return this.getI18n(i18nKey, 'MESSAGES.service_cost_module_info')
        },
        i18nInheritedFromBu() {
            return this.getI18n(i18nKey, 'MESSAGES.inherited_from_business_unit')
        },
        i18nPosContractTimeMinutes() {
            return this.getI18n(i18nKey, 'TITLES.pos_contract_time_minutes')
        },
        i18nPosVisitValue() {
            return this.getI18n(i18nKey, 'TITLES.pos_visit_value')
        },
        i18nPosVisitFrequency() {
            return this.getI18n(i18nKey, 'TITLES.pos_visit_frequency')
        },
        posAddContractTimePlaceholder() {
            const t = this.modalBusinessUnit.contract_time
            if (t === null || t === undefined || t === '') {
                return ''
            }
            return String(t)
        },
        posAddContractValuePlaceholder() {
            const v = this.modalBusinessUnit.contract_value
            if (v === null || v === undefined || v === '') {
                return ''
            }
            return this.getI18nCurrency(v)
        },
        modalOperation() {
            return this.isBusinessUnitCreated ? 'edited' : 'created'
        },
    },
    watch: {
        'posAddForm.selectedPos'(value) {
            if (value === null || value === undefined) {
                this.$set(this.posAddForm, 'selectedPos', {})
            }
        },
        'modalBusinessUnit.add_all_users': function (newValue, oldValue) {
            if (newValue && newValue !== oldValue) {
                this.modalBusinessUnit.users = []
            }
        },
        'modalBusinessUnit.add_all_pos': function (newValue, oldValue) {
            if (newValue && newValue !== oldValue) {
                this.modalBusinessUnit.point_of_sales = []
            }
        },
    },
    beforeDestroy() {
        this.removePosEscListener()
    },
    methods: {
        touchUserModalUserId() {
            const idValidation = this.$v.userModal && this.$v.userModal.user && this.$v.userModal.user.id
            if (idValidation && typeof idValidation.$touch === 'function') {
                idValidation.$touch()
            }
        },
        tabActivated(newTabIndex, oldTabIndex, event) {
            if (this.isEdit) {
                if (oldTabIndex === 0) {
                    this.$v.modalBusinessUnit.$touch()
                    if (this.$v.modalBusinessUnit.$invalid) {
                        event.preventDefault();
                    }
                }
            }
            if (newTabIndex === 1) {
                this.loadPos()
            } else if (newTabIndex === 2) {
                this.loadUsers()
            }
        },
        async loadUsers() {
            if (this.usersLoaded || this.loadingUsers) {
                return
            }

            if (!this.isBusinessUnitCreated) {
                this.usersLoaded = true
                return
            }

            this.loadingUsers = true
            await BusinessUnitsService.fetchById(this.modalBusinessUnit.id, {
                with: ['users'],
            }
            ).then((response) => {
                this.modalBusinessUnit.users = response.data.data.users
                this.usersLoaded = true
            })
            this.loadingUsers = false
        },
        async loadPos() {
            if (this.posLoaded || this.loadingPos) {
                return
            }

            if (!this.isBusinessUnitCreated) {
                this.posLoaded = true
                return
            }

            this.loadingPos = true
            await PosService.fetchAll({
                business_unit_id: [this.modalBusinessUnit.id],
                pagination: false,
            }).then((response) => {
                this.modalBusinessUnit.point_of_sales = response.data.data
                this.posLoaded = true
            })
            this.loadingPos = false
        },
        showModal() {
            this.tabIndex = 0
            this.isEdit = this.modalIsEdit
            this.toggleStatusOperation = null
            this.isToggleStatus = false
            this.submitLoading = false
            this.editLoading = false
            this.deleteLoading = false
            this.toggleStatusLoading = false
            this.loadingUsers = false
            this.loadingPos = false
            this.usersLoaded = false
            this.posLoaded = false
            this.posAddForm = {
                selectedPos: {},
                contract_time: '',
                contract_value: '',
                visit_frequency: '',
            }
            this.cancelPosInlineEdit()
            this.modalBusinessUnit = this.modal
            this.$refs.formModal.show()
        },

        resetValidation() {
            this.$nextTick(() => this.$v.$reset())
        },

        cleanModal() {
            this.replaceModalData({
                id: '',
                name: '',
                contract_value: 0,
                contract_time: 0,
                add_all_users: false,
                add_all_pos: false,
                users: [],
                point_of_sales: [],
            })
            this.posAddForm = {
                selectedPos: {},
                contract_time: '',
                contract_value: '',
                visit_frequency: '',
            }
            this.cancelPosInlineEdit()
            this.resetValidation()
        },

        replaceModalData(data) {
            Object.assign(this.modalBusinessUnit, data)
        },
        handleEdit() {
            this.editLoading = true

            this.loadUsers()
            this.loadPos()
            this.isEdit = true
            this.tabIndex = 0

            this.editLoading = false
        },
        handleToggleStatus(operation) {
            this.toggleStatusOperation = operation
            this.isToggleStatus = true

            if (operation === 'activate') {
                this.handleToggleStatusConfirmation()
            }
        },
        handleCancel() {
            if (this.isEdit) {
                this.$refs.confirmationFormModal.showModal(true, this.handleClose)
            }
            else {
                this.handleClose(true)
            }
        },
        handleCancelToggleStatus() {
            this.isToggleStatus = false
            this.toggleStatusOperation = null
        },

        handleClose(close) {
            if (!close) {
                return;
            }
            this.resetValidation()
            this.$refs.formModal.hide()
        },
        handleOk() {
            this.$v.modalBusinessUnit.$touch()
            if (!this.$v.modalBusinessUnit.$invalid && !this.submitLoading) {
                this.$refs.confirmationFormModal.showModal(false, this.handleSubmit)
            }
        },
        handleToggleStatusConfirmation() {
            this.$v.modalToggleStatus.$touch()
            if ((this.toggleStatusOperation === 'activate' || !this.$v.modalToggleStatus.$invalid) && !this.toggleStatusLoading) {
                this.$refs.confirmationFormModal.showModal(false, this.toggleStatusBusinessUnit)
            }
        },
        createOrUpdateBusinessUnit() {
            const payload = this.payloadForBusinessUnitSave()
            const operation = this.isBusinessUnitCreated
                ? BusinessUnitsService.update(this.modalBusinessUnit.id, payload)
                : BusinessUnitsService.create(payload)

            return operation
                .then((response) => {
                    this.replaceModalData(response.data.data)
                    return response
                })
                .catch((error) => {
                    this.unsuccessfulOperation()
                    return error
                })
        },

        async handleSubmit(submit) {
            if (!submit) {
                return;
            }

            this.submitLoading = true

            const operation = this.isBusinessUnitCreated ? 'edited' : 'created'

            const response = await this.createOrUpdateBusinessUnit()

            const statusCode = response.status.toString()

            if (statusCode.charAt(0) === '2') {
                this.successfulOperation(operation)
            }

            if (statusCode === '422') {
                this.modalResponseErrors = response.data.errors
            }

            this.submitLoading = false
        },
        successfulOperation(operation) {
            this.$emit('refresh')
            this.resetValidation()
            this.$refs.formModal.hide()
            this.positiveFeedback(this.i18nBusinessUnit, operation)
        },
        unsuccessfulOperation(operation) {
            this.negativeFeedback()
        },
        handleDelete() {
            if (this.deleteLoading) {
                return;
            }

            this.$refs.confirmationFormModal.showModal(false, this.deleteBusinessUnit)
        },
        async deleteBusinessUnit(deleteRecord) {
            if (!deleteRecord) {
                return;
            }

            this.$v.modalBusinessUnit.$touch()
            this.deleteLoading = true
            const response = await BusinessUnitsService.delete(this.modalBusinessUnit.id)
                .then((response) => response)
                .catch((error) => error)

            const statusCode = response.status.toString()

            if (statusCode.charAt(0) === '2') {
                this.$emit('refresh')
                this.resetValidation()
                this.$refs.formModal.hide()
                this.positiveFeedback(this.i18nBusinessUnit, 'deleted', 3)
            } else if (statusCode === '417') {
                this.$refs.feedbackModal.showModal(statusCode, response.data.message)
            } else if (statusCode === '422') {
                this.modalResponseErrors = response.data.errors
            } else {
                this.$refs.feedbackModal.showModal(statusCode, response.data.message)
            }

            this.deleteLoading = false
        },
        async toggleStatusBusinessUnit(confirmation) {
            if (!confirmation) return;

            this.toggleStatusLoading = true

            const operation = this.toggleStatusOperation === 'activate'
                ? BusinessUnitsService.activate(this.modalBusinessUnit.id, this.modalToggleStatus)
                : BusinessUnitsService.inactivate(this.modalBusinessUnit.id, this.modalToggleStatus)

            return operation
                .then((response) => {
                    this.replaceModalData(response.data.data)
                    this.$emit('refresh')
                    this.resetValidation()
                    this.toggleStatusLoading = false
                    this.toggleStatusOperation = null
                    this.isToggleStatus = false
                    return response
                })
                .catch((error) => {
                    this.unsuccessfulOperation()
                    this.toggleStatusLoading = false
                    return error
                })
        },
        async handleRemoveAllPos() {
            this.modalBusinessUnit.point_of_sales = []
        },
        async handleRemovePos(id) {
            const index = this.modalBusinessUnit.point_of_sales.findIndex(find => find.id === id);

            if (index > -1) {
                this.modalBusinessUnit.point_of_sales.splice(index, 1);
            }
        },
        payloadForBusinessUnitSave() {
            return {
                ...this.modalBusinessUnit,
                point_of_sales: this.modalBusinessUnit.point_of_sales.map((pos) =>
                    this.sanitizePointOfSaleForApi(pos)),
            }
        },
        sanitizePointOfSaleForApi(pos) {
            return omit(pos, [
                'contract_time_efetivo',
                'contract_value_efetivo',
                'visit_frequency_efetivo',
                'usando_fallback',
            ])
        },
        posPivotUsesFallback(item, aspect) {
            const uso = item.usando_fallback
            if (uso && typeof uso === 'object' && Object.prototype.hasOwnProperty.call(uso, aspect)) {
                return !!uso[aspect]
            }
            const snake = item[`usando_fallback_${aspect}`]
            if (snake !== undefined && snake !== null) {
                return !!snake
            }
            const camel = item[`${aspect}_usando_fallback`]
            if (camel !== undefined && camel !== null) {
                return !!camel
            }
            return false
        },
        posPivotCellMuted(item, field) {
            if (this.posPivotUsesFallback(item, field)) {
                return true
            }
            if (field === 'contract_time') {
                return item.contract_time === null || item.contract_time === undefined || item.contract_time === ''
            }
            if (field === 'contract_value') {
                return item.contract_value === null || item.contract_value === undefined || item.contract_value === ''
            }
            if (field === 'visit_frequency') {
                return item.visit_frequency === null || item.visit_frequency === undefined || item.visit_frequency === ''
            }
            return false
        },
        posEffectiveContractTime(item) {
            if (item.contract_time_efetivo !== undefined && item.contract_time_efetivo !== null) {
                return item.contract_time_efetivo
            }
            return this.modalBusinessUnit.contract_time
        },
        posEffectiveContractValue(item) {
            if (item.contract_value_efetivo !== undefined && item.contract_value_efetivo !== null) {
                return item.contract_value_efetivo
            }
            return this.modalBusinessUnit.contract_value
        },
        posEffectiveVisitFrequency(item) {
            if (item.visit_frequency_efetivo !== undefined && item.visit_frequency_efetivo !== null) {
                return item.visit_frequency_efetivo
            }
            return ''
        },
        formatPosContractTimeCell(item) {
            if (item.contract_time !== null && item.contract_time !== undefined && item.contract_time !== '') {
                return item.contract_time
            }
            const eff = this.posEffectiveContractTime(item)
            return eff === null || eff === undefined || eff === '' ? '—' : String(eff)
        },
        formatPosContractValueCell(item) {
            if (item.contract_value !== null && item.contract_value !== undefined && item.contract_value !== '') {
                return this.getI18nCurrency(item.contract_value)
            }
            const eff = this.posEffectiveContractValue(item)
            if (eff === null || eff === undefined || eff === '') {
                return '—'
            }
            return this.getI18nCurrency(eff)
        },
        formatPosVisitFrequencyCell(item) {
            if (item.visit_frequency !== null && item.visit_frequency !== undefined && item.visit_frequency !== '') {
                return item.visit_frequency
            }
            const eff = this.posEffectiveVisitFrequency(item)
            return eff === null || eff === undefined || eff === '' ? '—' : String(eff)
        },
        posInlineEditing(item, field) {
            return this.posInlineEdit.itemId === item.id && this.posInlineEdit.field === field
        },
        posInlineInvalid(item, field) {
            return !!this.posInlineInvalidMap[`${item.id}-${field}`]
        },
        clearPosInlineInvalid(item, field) {
            const key = `${item.id}-${field}`
            if (this.posInlineInvalidMap[key]) {
                this.$delete(this.posInlineInvalidMap, key)
            }
        },
        setPosInlineInvalid(item, field) {
            this.$set(this.posInlineInvalidMap, `${item.id}-${field}`, true)
        },
        installPosEscListener() {
            this.removePosEscListener()
            this.posEscBoundHandler = (e) => {
                if (e.key === 'Escape') {
                    this.cancelPosInlineEdit()
                }
            }
            document.addEventListener('keydown', this.posEscBoundHandler, true)
        },
        removePosEscListener() {
            if (this.posEscBoundHandler) {
                document.removeEventListener('keydown', this.posEscBoundHandler, true)
                this.posEscBoundHandler = null
            }
        },
        startPosInlineEdit(item, field) {
            if (!this.isEdit || this.submitLoading || this.loadingPos) {
                return
            }
            if (this.posInlineEditing(item, field)) {
                return
            }
            if (this.posInlineEdit.itemId !== null) {
                const prev = this.modalBusinessUnit.point_of_sales.find((p) => p.id === this.posInlineEdit.itemId)
                if (prev) {
                    if (!this.commitPosInlineEdit(prev, this.posInlineEdit.field)) {
                        return
                    }
                }
                else {
                    this.cancelPosInlineEdit()
                }
            }
            let draft = ''
            let backup = null
            if (field === 'contract_time') {
                backup = item.contract_time
                draft = backup !== null && backup !== undefined && backup !== '' ? String(backup) : ''
            }
            else if (field === 'contract_value') {
                backup = item.contract_value
                draft = backup !== null && backup !== undefined && backup !== '' ? String(backup) : ''
            }
            else if (field === 'visit_frequency') {
                backup = item.visit_frequency
                draft = backup !== null && backup !== undefined && backup !== '' ? String(backup) : ''
            }
            this.posInlineEdit = {
                itemId: item.id,
                field,
                draft: parseFloat(draft).toFixed(2),
                backup: parseFloat(backup).toFixed(2),
            }
            this.clearPosInlineInvalid(item, field)
            this.$nextTick(() => {
                const ref = field === 'visit_frequency' ? this.$refs.posInlineInputVf : this.$refs.posInlineInput
                if (ref && ref.focus) {
                    ref.focus()
                }
                if (field === 'contract_value' && this.$refs.posInlineMoney && this.$refs.posInlineMoney.$el) {
                    const el = this.$refs.posInlineMoney.$el
                    if (el) {
                        el.focus()
                    }
                }
                this.installPosEscListener()
            })
        },
        cancelPosInlineEdit() {
            this.removePosEscListener()
            if (this.posInlineEdit.itemId === null) {
                return
            }
            const item = this.modalBusinessUnit.point_of_sales.find((p) => p.id === this.posInlineEdit.itemId)
            if (item) {
                const f = this.posInlineEdit.field
                if (f === 'contract_time') {
                    this.$set(item, 'contract_time', this.posInlineEdit.backup)
                }
                else if (f === 'contract_value') {
                    this.$set(item, 'contract_value', this.posInlineEdit.backup)
                }
                else if (f === 'visit_frequency') {
                    this.$set(item, 'visit_frequency', this.posInlineEdit.backup)
                }
                this.clearPosInlineInvalid(item, f)
            }
            this.posInlineEdit = {
                itemId: null,
                field: null,
                draft: '',
                backup: null,
            }
        },
        commitPosInlineEdit(item, field) {
            if (!item || !field || this.posInlineEdit.itemId !== item.id || this.posInlineEdit.field !== field) {
                return true
            }
            const draft = this.posInlineEdit.draft
            if (field === 'contract_time') {
                const s = String(draft).trim()
                let nextVal = null
                if (s !== '') {
                    const n = parseInt(s, 10)
                    if (Number.isNaN(n) || n < 1) {
                        this.setPosInlineInvalid(item, field)
                        return false
                    }
                    nextVal = n
                }
                this.$set(item, 'contract_time', nextVal)
            }
            else if (field === 'contract_value') {
                const s = String(draft).trim()
                let nextVal = null
                if (s !== '') {
                    const n = parseFloat(s)
                    if (Number.isNaN(n) || n < 0) {
                        this.setPosInlineInvalid(item, field)
                        return false
                    }
                    nextVal = n
                }
                this.$set(item, 'contract_value', nextVal)
            }
            else if (field === 'visit_frequency') {
                const s = String(draft).trim()
                if (s === '') {
                    this.$set(item, 'visit_frequency', null)
                }
                else {
                    const n = parseInt(s, 10)
                    if (Number.isNaN(n) || n < 1) {
                        this.setPosInlineInvalid(item, field)
                        return false
                    }
                    this.$set(item, 'visit_frequency', n)
                }
            }
            this.clearPosInlineInvalid(item, field)
            this.removePosEscListener()
            this.posInlineEdit = {
                itemId: null,
                field: null,
                draft: '',
                backup: null,
            }
            return true
        },
        async handleAddPos() {
            this.$v.posAddForm.$touch()
            if (this.$v.posAddForm.$invalid || this.submitLoading) {
                return
            }
            const id = this.posAddForm.selectedPos && this.posAddForm.selectedPos.id
            if (!id) {
                return
            }
            const contractTime = this.parseOptionalPositiveIntOrNull(this.posAddForm.contract_time)
            const contractValue = this.parseOptionalMoneyOrNull(this.posAddForm.contract_value)
            const visitFrequency = this.parseOptionalPositiveIntOrNull(this.posAddForm.visit_frequency)
            const row = {
                ...this.posAddForm.selectedPos,
                contract_time: contractTime,
                contract_value: contractValue,
                visit_frequency: visitFrequency,
            }
            const index = this.modalBusinessUnit.point_of_sales.findIndex((p) => p.id === id)
            if (index < 0) {
                this.modalBusinessUnit.point_of_sales.push(row)
            }
            else {
                const existing = this.modalBusinessUnit.point_of_sales[index]
                this.$set(this.modalBusinessUnit.point_of_sales, index, {
                    ...existing,
                    contract_time: contractTime,
                    contract_value: contractValue,
                    visit_frequency: visitFrequency,
                })
            }
            this.posAddForm = {
                selectedPos: {},
                contract_time: '',
                contract_value: '',
                visit_frequency: '',
            }
            this.$v.posAddForm.$reset()
            if (this.$refs.posMultiselect && this.$refs.posMultiselect.$el) {
                this.$refs.posMultiselect.$el.focus()
            }
        },
        parseOptionalPositiveIntOrNull(value) {
            const s = String(value == null ? '' : value).trim()
            if (s === '') {
                return null
            }
            const n = parseInt(s, 10)
            if (Number.isNaN(n) || n < 1) {
                return null
            }
            return n
        },
        parseOptionalMoneyOrNull(value) {
            const s = String(value == null ? '' : value).trim()
            if (s === '') {
                return null
            }
            const n = parseFloat(s)
            if (Number.isNaN(n) || n < 0) {
                return null
            }
            return n
        },
        async handleAddUser() {
            this.$v.userModal.$touch()
            if (this.$v.userModal.$invalid || this.submitLoading) {
                return
            }

            const index = this.modalBusinessUnit.users.findIndex(find => find.id === this.userModal.user.id);
            if (index < 0) {
                this.modalBusinessUnit.users.push({
                    id: this.userModal.user.id,
                    name: this.userModal.user.name,
                    email: this.userModal.user.email,
                    monthly_cost: this.userModal.monthly_cost,
                    hours_daily: this.userModal.hours_daily,
                })

                Object.assign(this.userModal, {
                    user: {},
                    monthly_cost: 0,
                    hours_daily: 0,
                })

                this.resetValidation()
            }
            this.$refs.userMultiselect.$el.focus()
        },
        async handleRemoveAllUser() {
            this.modalBusinessUnit.users = []
        },
        async handleRemoveUser(id) {
            const index = this.modalBusinessUnit.users.findIndex(find => find.id === id);

            if (index > -1) {
                this.modalBusinessUnit.users.splice(index, 1);
            }
        },
        showPosCsvModal() {
            this.$nextTick(() => this.$refs.posCsvModal.showModal())
        },
        showUserCsvModal() {
            this.$nextTick(() => this.$refs.userCsvModal.showModal())
        },
        async loadPosByJson(csv) {
            const items = this.mapJson(
                csv.parsed,
                csv.fields_mapped,
                csv.has_headers
            )
            const data = await this.$refs.posCsvModal.fetchRelationItems(csv)

            if (!data || data.length === 0) {
                this.warningFeedback(this.i18nSelectThroughCsv, this.i18nNoRecordsFound)
                this.$refs.posCsvModal.hideModal()
                return
            }

            const invalidCodes = []
            const merged = data.map((pos) => {
                const row = items.find(
                    (i) =>
                        String(i.external_code).trim() ===
                        String(pos.external_code).trim()
                )
                if (!row) {
                    return pos
                }
                const next = { ...pos }
                const ctRaw = row.contract_time
                if (
                    ctRaw !== null &&
                    ctRaw !== undefined &&
                    String(ctRaw).trim() !== ''
                ) {
                    const t = parseInt(String(ctRaw).trim(), 10)
                    if (Number.isNaN(t) || t < 1) {
                        invalidCodes.push(String(pos.external_code))
                    }
                    else {
                        next.contract_time = t
                    }
                }
                const cvRaw = row.contract_value
                if (
                    cvRaw !== null &&
                    cvRaw !== undefined &&
                    String(cvRaw).trim() !== ''
                ) {
                    const v = parseFloat(String(cvRaw).trim())
                    if (Number.isNaN(v) || v < 0) {
                        invalidCodes.push(String(pos.external_code))
                    }
                    else {
                        next.contract_value = v
                    }
                }
                const vfRaw = row.visit_frequency
                if (
                    vfRaw !== null &&
                    vfRaw !== undefined &&
                    String(vfRaw).trim() !== ''
                ) {
                    const f = parseInt(String(vfRaw).trim(), 10)
                    if (Number.isNaN(f) || f < 1) {
                        invalidCodes.push(String(pos.external_code))
                        return null
                    }
                    next.visit_frequency = f
                }
                return next
            })

            const ok = merged.filter(Boolean)
            if (invalidCodes.length > 0) {
                const unique = [...new Set(invalidCodes)]
                const msg = this.getI18n(i18nKey, 'MESSAGES.pos_import_validation_errors').replace(
                    '{codes}',
                    unique.join(', ')
                )
                this.warningFeedback(this.i18nSelectThroughCsv, msg)
            }

            if (ok.length > 0) {
                this.modalBusinessUnit.point_of_sales = unionBy(
                    this.modalBusinessUnit.point_of_sales,
                    ok,
                    'id'
                )
                if (invalidCodes.length === 0) {
                    this.successFeedback(this.i18nSelectThroughCsv, this.i18nSuccessfullyProcessed)
                }
            }
            else if (invalidCodes.length === 0) {
                this.warningFeedback(this.i18nSelectThroughCsv, this.i18nNoRecordsFound)
            }

            this.$refs.posCsvModal.setLoadingThirdStep(false)
            this.$refs.posCsvModal.hideModal()
        },
        async loadUsersByJson(csv) {
            const data = await this.$refs.userCsvModal.fetchRelationItems(csv)

            if (data && data.length > 0) {
                this.modalBusinessUnit.users = unionBy(this.modalBusinessUnit.users, data, 'id')
                this.successFeedback(this.i18nSelectThroughCsv, this.i18nSuccessfullyProcessed)
            } else {
                this.warningFeedback(this.i18nSelectThroughCsv, this.i18nNoRecordsFound)
            }

            this.$refs.userCsvModal.hideModal()
        },
    },
}
</script>

<style scoped>
/* Match PDV modal: keep label actions on one row (inline-block + nowrap); flex-wrap was stacking in col-sm-6 */
.business-unit-modal__toolbar-actions ::v-deep .btn + .btn {
    margin-left: 0.5rem;
}

.business-unit-pos-editable-cell {
    min-height: 1.5rem;
    padding: 0.125rem 0.25rem;
    cursor: pointer;
    border-radius: 0.25rem;
}

.business-unit-pos-editable-cell:hover {
    background-color: rgba(0, 0, 0, 0.04);
}

.business-unit-pos-editable-cell--invalid {
    background-color: rgba(220, 53, 69, 0.06);
    outline: 1px solid #dc3545;
}

.business-unit-pos-editable-cell__inherit-icon {
    font-size: 0.85rem;
    color: #6c757d;
    vertical-align: middle;
}
</style>
